import { Text } from "@certn/cascadia";
import { Address } from "@certn/cascadia/patterns/address-form/types";
import { Divider } from "@certn/cascadia-react";
import { CascadiaJsonFormContext } from "@certn/forms/src/CascadiaJsonFormContext";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { BaseLayoutContent } from "@certn/forms/src/layouts/BaseLayoutContent";
import { formatCapitalizedString } from "@certn/utils";
import { css, cx } from "@emotion/css";
import { ControlProps } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { set, get } from "lodash";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { useApplication } from "./ApplicationContext";

interface NameType {
  type?: "LEGAL" | "FORMER" | "ALIAS" | "MAIDEN";
  title?: string;
  given_name?: string;
  family_name?: string;
  additional_name?: string;
  initials?: string;
}

interface Residence {
  address: Address;
  start_date?: string;
  end_date?: string | null;
}

export function CertificateDeliveryOptionControl(props: ControlProps) {
  const { dbs_paper_certificate } = props.data;
  const { clientGroupName, clientGroupAddress } = useApplication();
  const paperCertificateWanted =
    dbs_paper_certificate?.receive_paper_certificate;
  const currentAddressSelected =
    dbs_paper_certificate?.certificate_delivery_option === "CURRENT";
  const clientGroupAddressSelected =
    dbs_paper_certificate?.certificate_delivery_option === "CLIENT_GROUP";

  const updatedSchema = { ...props.schema };
  const path =
    "properties.dbs_paper_certificate.properties.certificate_delivery_option.oneOf";
  const currentOneOf = get(props.schema, path, []);
  set(updatedSchema, path, [
    { const: "CLIENT_GROUP", title: clientGroupName },
    ...currentOneOf.filter((option) => option.const !== "CLIENT_GROUP"),
  ]);

  const legalName = props.data.name;
  const currentAddress = props.data?.residences.find(
    (item: Residence) => item.end_date === null
  );

  const nameAndAddressContent = getNameAndAddressContent(
    {
      given_name: formatCapitalizedString(legalName?.given_name ?? ""),
      family_name: formatCapitalizedString(legalName?.family_name ?? ""),
      additional_name: formatCapitalizedString(
        legalName?.additional_name ?? ""
      ),
    },
    currentAddress
  );
  const clientGroupNameContent = getNameAndAddressContent(
    { given_name: clientGroupName },
    { address: clientGroupAddress ?? {} }
  );

  return (
    <BaseLayout
      testID="certificate-delivery-layout"
      {...props}
      schema={updatedSchema}
      direction="column"
    >
      <BaseLayoutContent />
      {paperCertificateWanted && currentAddressSelected ? (
        <NameAndAddress contentArray={nameAndAddressContent} />
      ) : clientGroupAddressSelected ? (
        <NameAndAddress contentArray={clientGroupNameContent} />
      ) : null}
    </BaseLayout>
  );
}

const styles = {
  container: css({
    paddingLeft: "var(--space-7)",
    paddingBottom: "var(--space-4)",
  }),
  textBlock: css({
    display: "block",
  }),
  summaryHeading: css({
    paddingLeft: "var(--space-4)",
  }),
};

export const CertificateDeliveryOptionControlRenderer =
  withJsonFormsControlProps(CertificateDeliveryOptionControl);

function getNameAndAddressContent(legalName: NameType, address: Residence) {
  const { given_name, family_name, additional_name } = legalName;
  const {
    street_address,
    unit_number,
    locality,
    administrative_division_2,
    postal_code,
    country,
  } = address?.address || {};

  const content = [
    `${given_name ? given_name : ""} ${additional_name ? additional_name : ""} ${family_name ? family_name : ""}`,
    street_address,
    unit_number,
    locality && administrative_division_2
      ? `${locality}, ${administrative_division_2}`
      : locality || administrative_division_2,
    postal_code,
    country,
  ].filter(Boolean);

  return content;
}

interface Props {
  contentArray: string[];
}

function NameAndAddress(props: Props) {
  const { t } = useTranslation();
  const { isSummary } = useContext(CascadiaJsonFormContext);
  const { contentArray } = props;

  if (!contentArray) {
    return (
      <Text className={styles.textBlock} color="text-weak" style="input/label">
        {t("Name and address missing.")}
      </Text>
    );
  }

  return (
    <>
      <Divider />
      <Text
        style="form/section-heading"
        color="text-base"
        className={cx(isSummary && styles.summaryHeading)}
      >
        {t("Send to")}:
      </Text>
      <div className={styles.container}>
        {contentArray ? (
          contentArray.map((item, i) => {
            return (
              <Text
                key={i}
                className={styles.textBlock}
                color="text-weak"
                style="input/label"
              >
                {item}
              </Text>
            );
          })
        ) : (
          <Text
            className={styles.textBlock}
            color="text-weak"
            style="input/label"
          >
            {t("Name and address missing.")}
          </Text>
        )}
      </div>
    </>
  );
}
